import { useQuery } from '@apollo/client';
import { Close } from '@mui/icons-material';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { Dialog, IconButton, Skeleton } from '@mui/material';
import type React from 'react';

import { buildAllMFQ, buildSharedAllMFQ } from 'src/dynamoQueries/queryHelpers';
import {
  MediaPreviewRouter,
  useStyles,
} from 'src/HomePage/DrawerAndPushedContents/PushedContents/AkomiMediaCardGrid/AkomiMediaCard/MediaCardPreviewModal';
import { getMetadataFieldValue } from 'src/helpers/common';
import { useMediaQueries } from 'src/hooks/media';

interface MediaPreviewModalWithProxyProps {
  itemId?: string;
  itemType?: string;
  isShared?: boolean;
  onClose: () => void;
  onNext?: () => void;
  onPrev?: () => void;
}

const MediaPreviewModalWithProxy: React.FC<MediaPreviewModalWithProxyProps> = ({
  itemId = '',
  itemType,
  isShared,
  onClose,
  onNext,
  onPrev,
}) => {
  const classes = useStyles();
  const { proxyFormat } = useMediaQueries(itemId, isShared ?? false);

  const { data: formatData, loading: loadingMimeType } = useQuery(isShared ? buildSharedAllMFQ() : buildAllMFQ(), {
    skip: !proxyFormat?.id,
    variables: { globalId: proxyFormat?.id, definition_Name_In: 'mimetype' },
  });

  const mimetype = getMetadataFieldValue(formatData, 'mimetype');

  return (
    <Dialog
      open={true}
      onClose={onClose}
      fullScreen
      PaperProps={{
        sx: {
          bgcolor: 'transparent',
          overflow: 'hidden',
          boxShadow: 'none',
        },
      }}
      slotProps={{
        backdrop: {
          sx: {
            bgcolor: (theme) => (theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.92)' : 'rgba(0, 0, 0, 0.85)'),
          },
        },
      }}
    >
      <IconButton
        className={classes.closeIcon}
        onClick={onClose}
        size="large"
        sx={{ color: (theme) => theme.palette.common.white }}
      >
        <Close />
      </IconButton>
      {onNext && (
        <IconButton
          className={classes.nextIcon}
          onClick={onNext}
          size="large"
          sx={{ color: (theme) => theme.palette.common.white }}
        >
          <NavigateNextIcon />
        </IconButton>
      )}
      {onPrev && (
        <IconButton
          className={classes.prevIcon}
          onClick={onPrev}
          size="large"
          sx={{ color: (theme) => theme.palette.common.white }}
        >
          <NavigateBeforeIcon />
        </IconButton>
      )}
      {loadingMimeType ? (
        <Skeleton
          variant="rectangular"
          animation="wave"
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '60%',
            height: '60%',
            borderRadius: 2,
            bgcolor: (theme) => theme.palette.action.disabledBackground,
          }}
        />
      ) : (
        <MediaPreviewRouter
          id={itemId}
          mimetype={mimetype}
          format={proxyFormat}
          isShared={isShared}
          itemType={itemType}
        />
      )}
    </Dialog>
  );
};

export default MediaPreviewModalWithProxy;
